import { ApiResult } from 'src/Libs/api'
import { AuthFetchWithResponse } from 'src/Types/api'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { handleFetchError } from 'src/Libs/api/utils'
import { InstallationStatementApiDto, InstallationStatementModel } from 'src/Libs/installation-statement/models/statement'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { UnexpectedApiResponseError } from 'src/Libs/errors'
import { DeviceInfoForStatement } from 'src/Libs/installation-statement/components/Modals/InstallationStatementDialog'
import { CompanyLocationDto } from 'src/Libs/user/api/dto/user.dto'
import { Company } from '../models/company'
import { CompanyInformationDto } from '../dto'
import { isFlowTheme } from 'src/Libs/utils'

export const getInstallationStatement = async (
  installation: DeviceInfoForStatement,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<InstallationStatementApiDto, AuthFetchError | Error>> => {
  try {
    const response = await authFetch<InstallationStatementApiDto>({
      path: `installations/${installation.id}/device-links/${installation.deviceLinkId}/statements`,
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<InstallationStatementApiDto, AuthFetchError | Error>(response.data, false)
    }
    return new ApiResult<InstallationStatementApiDto, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<InstallationStatementApiDto>(error)
  }
}

export const saveInstallationStatement = async (
  deviceLinkId: number,
  installationId: number,
  payload: InstallationStatementApiDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number, AuthFetchError | Error>> => {
  try {
    const { installerName, installerSurname, installationStatementFiles, ...filteredPayload } = payload
    const { installationStatementId, ...filteredStorage } = filteredPayload.storage
    const newPayload = {
      ...filteredPayload,
      storage: filteredStorage,
    }
    const response = await authFetch<number>({
      path: `installations/${installationId}/device-links/${deviceLinkId}/statements`,
      method: 'POST',
      payload: newPayload,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })
    if (response.status !== StatusCodes.CREATED) {
      throw new UnexpectedApiResponseError(response.status)
    }
    return new ApiResult<number, AuthFetchError | Error>(response.data, false)
  } catch (error) {
    return handleFetchError<number>(error)
  }
}

export const confirmInstallationStatement = async (
  deviceLinkId: number,
  installationId: number,
  payload: InstallationStatementApiDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<InstallationStatementModel, AuthFetchError | Error>> => {
  try {
    const { installerName, installerSurname, installationStatementFiles, ...filteredPayload } = payload
    const { installationStatementId, ...filteredStorage } = filteredPayload.storage
    const newPayload = {
      ...filteredPayload,
      storage: filteredStorage,
    }
    const response = await authFetch<InstallationStatementModel>({
      path: `installations/${installationId}/device-links/${deviceLinkId}/statements/confirm`,
      method: 'POST',
      payload: newPayload,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<InstallationStatementModel, AuthFetchError | Error>(response.data, false)
    }
    return new ApiResult<InstallationStatementModel, AuthFetchError | Error>(null, false)
  } catch (error) {
    return handleFetchError<InstallationStatementModel>(error)
  }
}

export const getCompanyInfo = async (companyId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<Company, AuthFetchError | Error>> => {
  if (isFlowTheme) return new ApiResult<Company, AuthFetchError | Error>(null, false)
  try {
    const response = await authFetch<Company>({
      path: `users/companies/${companyId}`,
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<Company, AuthFetchError | Error>(response.data, false)
    }
    return new ApiResult<Company, AuthFetchError | Error>(null, false)
  } catch (error) {
    return handleFetchError<Company>(error)
  }
}

export const getCompanyAddressInfo = async (
  companyId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<CompanyLocationDto, AuthFetchError | Error>> => {
  if (isFlowTheme) return new ApiResult<CompanyLocationDto, AuthFetchError | Error>(null, false)
  try {
    const response = await authFetch<CompanyLocationDto>({
      path: `users/companies/${companyId}/location`,
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<CompanyLocationDto, AuthFetchError | Error>(response.data, false)
    }
    return new ApiResult<CompanyLocationDto, AuthFetchError | Error>(null, false)
  } catch (error) {
    return handleFetchError<CompanyLocationDto>(error)
  }
}

export const releaseInstallationStatement = async (
  installationId: string | undefined,
  deviceId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch({
      path: `installations/${installationId}/device-links/${deviceId}/statements/release`,
      method: 'PUT',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult(null, false)
    }

    return new ApiResult(null, true)
  } catch (error) {
    throw new ApiResult(null, true, error)
  }
}

export const getCompanyNameUsingPartnerNumber = async (
  partnerNumber: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<CompanyInformationDto, AuthFetchError | Error>> => {
  try {
    const response = await authFetch<CompanyInformationDto>({
      path: `users/companies/partner-number`,
      payload: {
        partnerNumber: partnerNumber,
      },
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<CompanyInformationDto, AuthFetchError | Error>(response.data, false)
    }
    return new ApiResult<CompanyInformationDto, AuthFetchError | Error>(null, false)
  } catch (error) {
    return handleFetchError<CompanyInformationDto>(error)
  }
}
